import { MathUtil } from "@base/math/MathUtil";
import { lec } from "@base/util/Comma";
import { ICalculatedPoleLength } from "@biz/definitions/MountDefinitons";
import { ISunSailData, MaterialType, PoleId, PoleType } from "@biz/definitions/SunSailDefinitions";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";

interface IOptimizePoleChargeHelper {
  poleId: PoleId;
  totalLength: number;
  shoppingLengthCharged: number;
  shoppingLength: number;
  material: MaterialType;
  sharedWithPole: PoleId | undefined;
  clientAvailable?: boolean;
}

export class PoleChargeOptimizer {
  static #calcInitialPoleLength(sunSail: ISunSailData, poleId: PoleId): ICalculatedPoleLength {
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const totalLength = MathUtil.round(SunSailLogic.getPoleLength(sunSail, poleId), 2);
    const shoppingLength = PoleLogic.isDroppole(poleMaterial) ? MathUtil.clamp(Math.ceil(MathUtil.round(totalLength, 2)), 3, 5) : (totalLength <= 3.01 ? 3 : 6)

    return {
      totalLength,
      bottomAdd: SunSailLogic.calcBottomPoleExtension(sunSail, poleId),
      topAdd: SunSailLogic.calcTopPoleExtension(sunSail, poleId),
      shoppingLength,
      shoppingLengthCharged: shoppingLength
    };
  }

  static calcPoleLengthInfoFn(sunSail: ISunSailData): (poleId: PoleId) => ICalculatedPoleLength {
    const calculated: { corner: ICalculatedPoleLength[], center: ICalculatedPoleLength[] } = { corner: [], center: [] }
    const getCalculatedPoleLength = (poleId: PoleId): ICalculatedPoleLength => {
      const pid = Poles.fromPoleId(poleId);
      return calculated[pid.polePositionType][pid.poleIndex];
    }

    const poleIds = Poles.getPoleIds(sunSail);
    for (const poleId of poleIds) {
      const pid = Poles.fromPoleId(poleId);
      calculated[pid.polePositionType].push(PoleChargeOptimizer.#calcInitialPoleLength(sunSail, poleId));
    }

    const optimizePoleChargeHelper: IOptimizePoleChargeHelper[] = [];
    for (const poleId of poleIds) {
      const poleType = SunSailLogic.getPoleType(sunSail, poleId)
      const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
      const optimizableMaterial = PoleLogic.isAlu(poleMaterial) || PoleLogic.isSteel(poleMaterial) || PoleLogic.isIron(poleMaterial);

      if (poleType === PoleType.Pole && optimizableMaterial) {
        const poleArticle = SunSailLogic.calcPoleShoppingArticle(Object.values(sunSail.shoppingList.items), poleId);

        const pl = getCalculatedPoleLength(poleId);
        optimizePoleChargeHelper.push({
          poleId,
          totalLength: pl.totalLength,
          shoppingLengthCharged: poleArticle?.countClientAvailable ? 0 : pl.shoppingLength,
          shoppingLength: pl.shoppingLength,
          material: poleMaterial,
          sharedWithPole: undefined,
          clientAvailable: !!poleArticle?.countClientAvailable
        })
      }
    }

    optimizePoleChargeHelper.sort((a, b) => b.totalLength - a.totalLength);
    for (let i = 0; i < optimizePoleChargeHelper.length; i++) {
      const ch1 = optimizePoleChargeHelper[i];
      if (ch1.totalLength > 3.0 && !ch1.clientAvailable) { // Masten unter 3m kosten sowieso 3m, Masten wie z.b. 3.1 können geteilt werden und kosten dann 3 statt 6m
        const rest = 6 - ch1.totalLength;
        for (let j = i + 1; j < optimizePoleChargeHelper.length; j++) {
          const other = optimizePoleChargeHelper[j];
          if (lec(other.totalLength, rest, 2) && other.sharedWithPole === undefined && other.material === ch1.material && !other.clientAvailable) {
            ch1.sharedWithPole = other.poleId;
            other.sharedWithPole = ch1.poleId;
            ch1.shoppingLengthCharged = 3;
            ch1.shoppingLength = 3;
            break;
          }
        }
      }
    }

    //  && !ch1.clientAvailable

    for (const ch of optimizePoleChargeHelper) {
      const pl = getCalculatedPoleLength(ch.poleId);
      pl.shoppingLength = ch.shoppingLength;
      pl.shoppingLengthCharged = ch.shoppingLengthCharged;
    }

    return getCalculatedPoleLength
  }
}